import { useEffect, useState } from "react";
import { Plus, Trash, ArrowUUpLeft } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { inr } from "@/lib/constants";
import { PageHeader, EmptyState, Stat } from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { DatePicker } from "@/components/DatePicker";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";

const today = () => new Date().toISOString().slice(0, 10);
const EMPTY = { invoice_id: "", amount: "", reason: "job_cancelled", issued_on: today(), notes: "" };
const REASONS = { job_cancelled: "Job cancelled", fee_revised: "Fee revised", duplicate: "Duplicate invoice", gst_correction: "GST correction", other: "Other" };

export default function CreditNotes() {
  const [notes, setNotes] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);

  const load = () => api.get("/coll/credit_notes").then((r) => setNotes(r.data)).catch(() => setNotes([]));
  useEffect(() => {
    load();
    api.get("/invoices").then((r) => setInvoices(r.data)).catch(() => {});
  }, []);

  const set = (k) => (e) => setForm({ ...form, [k]: e?.target ? e.target.value : e });
  const invOf = (id) => invoices.find((i) => i.id === id);

  const save = async (e) => {
    e.preventDefault();
    const amount = parseFloat(form.amount);
    if (!form.invoice_id) { toast.error("Pick the original invoice"); return; }
    if (!amount || amount <= 0) { toast.error("Enter a valid amount"); return; }
    const inv = invOf(form.invoice_id);
    try {
      await api.post("/coll/credit_notes", {
        ...form, amount,
        number: `CN-${String((notes?.length || 0) + 1).padStart(4, "0")}`,
        invoice_number: inv?.invoice_number, bill_to_name: inv?.bill_to_name,
      });
      toast.success("Credit note issued");
      setOpen(false); setForm(EMPTY); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const remove = async (id) => {
    try { await api.delete(`/coll/credit_notes/${id}`); toast.success("Deleted"); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  if (!notes) return <div className="text-muted-foreground font-mono text-sm animate-pulse">Loading credit notes…</div>;

  const total = notes.reduce((s, n) => s + (n.amount || 0), 0);

  return (
    <div className="space-y-6" data-testid="credit-notes-page">
      <PageHeader
        overline="Invoices" title="Credit Notes"
        subtitle="Adjustment documents for cancelled or revised jobs, linked back to the original invoice"
        action={<Button onClick={() => { setForm(EMPTY); setOpen(true); }} className="rounded-full bg-primary hover:bg-[#113A64]" data-testid="add-credit-note-btn"><Plus size={16} className="mr-1" /> Issue Credit Note</Button>}
      />

      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
        <Stat label="Credit notes" value={notes.length} testid="stat-cn-count" />
        <Stat label="Total adjusted" value={inr(total)} tone="text-rose-600" testid="stat-cn-total" />
        <Stat label="This month" value={notes.filter((n) => (n.issued_on || "").startsWith(today().slice(0, 7))).length} testid="stat-cn-month" />
      </div>

      {notes.length === 0 ? (
        <EmptyState icon={ArrowUUpLeft}>No credit notes issued. Raise one when a job is cancelled or a fee is revised after billing.</EmptyState>
      ) : (
        <div className="space-y-2">
          {notes.map((n) => (
            <Card key={n.id} className="p-4 flex flex-wrap items-center justify-between gap-3 border-border" data-testid={`credit-note-${n.id}`}>
              <div className="min-w-0">
                <div className="font-mono text-xs text-accent">{n.number}</div>
                <div className="text-sm font-medium text-primary truncate">{n.bill_to_name}</div>
                <div className="text-xs text-muted-foreground">Against {n.invoice_number} · {REASONS[n.reason] || n.reason} · {n.issued_on}</div>
                {n.notes && <div className="text-xs text-muted-foreground mt-0.5">{n.notes}</div>}
              </div>
              <div className="flex items-center gap-3 shrink-0">
                <div className="font-display font-bold text-rose-600 tabular">−{inr(n.amount)}</div>
                <button className="text-destructive" onClick={() => remove(n.id)} data-testid={`delete-cn-${n.id}`}><Trash size={15} /></button>
              </div>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">Issue Credit Note</DialogTitle>
            <DialogDescription>Adjust a previously issued invoice.</DialogDescription>
          </DialogHeader>
          <form onSubmit={save} className="space-y-3" data-testid="credit-note-form">
            <div className="space-y-1.5"><Label>Original Invoice *</Label>
              <Select value={form.invoice_id} onValueChange={(v) => { const inv = invOf(v); setForm({ ...form, invoice_id: v, amount: inv ? String(inv.total) : "" }); }}>
                <SelectTrigger data-testid="cn-invoice"><SelectValue placeholder="Select invoice" /></SelectTrigger>
                <SelectContent>{invoices.map((i) => <SelectItem key={i.id} value={i.id}>{i.invoice_number} · {i.bill_to_name} · {inr(i.total)}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Amount (₹) *</Label><Input type="number" step="0.01" required value={form.amount} onChange={set("amount")} data-testid="cn-amount" /></div>
              <div className="space-y-1.5"><Label>Issued On</Label><DatePicker value={form.issued_on} onChange={set("issued_on")} placeholder="Select date" className="w-full" data-testid="cn-date" /></div>
            </div>
            <div className="space-y-1.5"><Label>Reason</Label>
              <Select value={form.reason} onValueChange={set("reason")}>
                <SelectTrigger data-testid="cn-reason"><SelectValue /></SelectTrigger>
                <SelectContent>{Object.entries(REASONS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5"><Label>Notes</Label><Textarea rows={2} value={form.notes} onChange={set("notes")} data-testid="cn-notes" /></div>
            <DialogFooter><Button type="submit" className="rounded-full bg-primary hover:bg-[#113A64]" data-testid="save-cn-btn">Issue Credit Note</Button></DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
